import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Table, Copy, MagnifyingGlass } from "@phosphor-icons/react";
import { api, http } from "@/lib/api";
import CsvPasteImport from "@/components/CsvPasteImport";

// Sprint 63.5 — per-source manual entry grid over the ACTIVE slate's playable
// pool. Ownership is a per-slate number, so this is deliberately slate-focused.
// Entries merge with any CSV upload for the same source; autosave on blur.
const DEFAULT_SLOTS = ["DFSARMY", "STOKASTIC", "SABERSIM"];
const SLOT_LABELS = { DFSARMY: "DFS Army", STOKASTIC: "Stokastic", SABERSIM: "SaberSim" };
const prettySource = (s) => SLOT_LABELS[String(s).toUpperCase()] || s;
const POS = ["ALL", "QB", "RB", "WR", "TE", "K", "DST"];
// Mirrors backend consensus.normalize_name exactly.
const normKey = (n) => String(n || "").toLowerCase().trim().replace(/[.'`]/g, "").replace(/\s+(jr|sr|ii|iii|iv|v)\b\.?/g, "").replace(/\s+/g, " ").trim();

export default function CompetitorPoolGrid() {
  const [pool, setPool] = useState([]);
  const [sources, setSources] = useState([]);
  const [tab, setTab] = useState(null);
  const [entries, setEntries] = useState({});
  const [pos, setPos] = useState("ALL");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(200);
  const [coverage, setCoverage] = useState(null);
  const [unsaved, setUnsaved] = useState(0);
  // Sprint 86 — ownership is a per-slate number, so sources are entered INTO a
  // slate. Pick it here instead of having to apply that slate first.
  const [slates, setSlates] = useState([]);
  const [slateDg, setSlateDg] = useState(null);
  const dirty = useRef({});
  const saveTimer = useRef(null);

  const loadPool = () => api.getPool(1000).then((r) => {
    // Showdown rosters kickers, so K stays in the grid; classic DK pools have none.
    const rows = (r?.rows || []).filter((p) => Number(p.our_proj || 0) > 0);
    rows.sort((a, b) => b.salary - a.salary);
    setPool(rows);
  }).catch(() => setPool([]));
  const loadSources = () => api.summary().then((s) => {
    const act = s?.competitor_sources || [];
    setSources(act);
    setTab((t) => t || act[0] || DEFAULT_SLOTS[0]);
  }).catch(() => {});
  const loadEntries = (src, dg) => http.get(`/competitors/${src}/players`, { params: { dg: dg ?? slateDg ?? undefined } })
    .then((r) => setEntries(r.data.entries || {})).catch(() => setEntries({}));
  const loadCoverage = () => http.get("/competitors/coverage").then((r) => setCoverage(r.data)).catch(() => {});
  const loadSlates = () => http.get("/competitors/slates").then((r) => {
    setSlates(r.data.slates || []);
    setSlateDg((cur) => cur ?? r.data.working_dg ?? r.data.applied_dg ?? null);
  }).catch(() => {});
  useEffect(() => { loadPool(); loadSources(); loadCoverage(); loadSlates(); }, []);
  useEffect(() => {
    const on = () => { flush(); setTab(null); loadPool(); loadSources(); loadCoverage(); };
    window.addEventListener("wp:slate-changed", on);
    window.addEventListener("wp:field-model-changed", on);
    return () => { window.removeEventListener("wp:slate-changed", on); window.removeEventListener("wp:field-model-changed", on); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => { if (tab) loadEntries(tab); }, [tab, slateDg]);
  useEffect(() => {
    const onHide = () => { if (Object.keys(dirty.current).length) flush(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onHide);
      onHide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sources]);

  const [extraTabs, setExtraTabs] = useState([]);
  const tabs = useMemo(() => {
    const base = [...sources, ...DEFAULT_SLOTS.filter((d) => !sources.includes(d))].slice(0, Math.max(3, sources.length));
    return [...base, ...extraTabs.filter((t) => !base.includes(t))];
  }, [sources, extraTabs]);
  const addTab = () => {
    const raw = window.prompt("Label for the new source (letters/numbers, e.g. RUNPURE):");
    const label = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);
    if (!label) return;
    setExtraTabs((t) => (t.includes(label) ? t : [...t, label]));
    flush(); setTab(label);
  };
  const visible = useMemo(() => pool
    .filter((p) => pos === "ALL" || p.position === pos)
    .filter((p) => !q || `${p.name} ${p.team}`.toLowerCase().includes(q.toLowerCase()))
    .slice(0, limit), [pool, pos, q, limit]);
  const isShowdown = useMemo(() => !!(slates.find((s) => s.dg === slateDg) || {}).is_showdown, [slates, slateDg]);
  const entered = pool.filter((p) => entries[normKey(p.name)]?.own != null || entries[normKey(p.name)]?.proj != null).length;
  const ownEntered = pool.filter((p) => entries[normKey(p.name)]?.own != null).length;

  const flush = async () => {
    const batch = Object.values(dirty.current);
    if (!batch.length) return;
    dirty.current = {};
    try {
      const r = await http.put(`/competitors/${tab}/players`, { entries: batch }, { params: { dg: slateDg ?? undefined } });
      setUnsaved(0);
      setCoverage(r.data.coverage || null);
      if (!sources.includes(r.data.source)) loadSources();
    } catch (e) {
      batch.forEach((b) => { dirty.current[normKey(b.name)] = b; });
      setUnsaved(Object.keys(dirty.current).length);
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const setField = (p, field, raw) => {
    const k = normKey(p.name);
    const v = raw === "" ? null : Number(raw);
    if (raw !== "" && Number.isNaN(v)) return;
    setEntries((e) => {
      const cur = { name: p.name, proj: null, own: null, own_cpt: null, own_flex: null, proj_cpt: null, ...(e[k] || {}) };
      cur[field] = v;
      // On a showdown slate the two halves are what you type; the total is
      // the share of lineups containing the player, i.e. CPT + FLEX.
      if (isShowdown && (field === "own_cpt" || field === "own_flex")) {
        const c = field === "own_cpt" ? v : cur.own_cpt;
        const f = field === "own_flex" ? v : cur.own_flex;
        cur.own = c == null && f == null ? null : Math.round(Math.min(100, (c || 0) + (f || 0)) * 10) / 10;
      }
      dirty.current[k] = {
        name: p.name, proj: cur.proj ?? null, own: cur.own ?? null,
        own_cpt: cur.own_cpt ?? null, own_flex: cur.own_flex ?? null, proj_cpt: cur.proj_cpt ?? null,
      };
      return { ...e, [k]: cur };
    });
    setUnsaved(Object.keys(dirty.current).length + 1);
    // Autosave on a short debounce — blur alone lost a whole grid of typing
    // when the tab was closed or the slate reloaded before the last blur.
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flush(), 1200);
  };
  const copyFrom = async (from) => {
    if (!from || from === tab) return;
    try {
      const r = await http.get(`/competitors/${from}/players`);
      const src = r.data.entries || {};
      const batch = Object.values(src).filter((e) => e.proj != null || e.own != null).map((e) => ({
        name: e.name, proj: e.proj, own: e.own,
        own_cpt: e.own_cpt ?? null, own_flex: e.own_flex ?? null, proj_cpt: e.proj_cpt ?? null,
      }));
      if (!batch.length) { toast.info(`${from} has no entries yet`); return; }
      await http.put(`/competitors/${tab}/players`, { entries: batch });
      toast.success(`Copied ${batch.length} players from ${from} into ${tab} — now edit the deltas`);
      loadEntries(tab); loadSources(); loadCoverage();
    } catch (e) { toast.error("Copy failed"); }
  };
  const clearTab = async () => {
    if (!window.confirm(`Remove every ${tab} entry for this account?`)) return;
    try { await api.deleteCompetitor(tab); setEntries({}); loadSources(); loadCoverage(); toast.info(`${tab} cleared`); } catch { toast.error("Clear failed"); }
  };
  if (!tab) return null;
  return (
    <section className="bg-[#121212] border border-[#FFCC00]/40 p-4" data-testid="competitor-pool-grid">
      {slates.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3 border border-zinc-800 bg-black/40 px-2.5 py-2"
             data-testid="competitor-slate-picker">
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Entering for</span>
          <select
            value={slateDg ?? ""}
            onChange={(e) => { flush(); setSlateDg(Number(e.target.value)); }}
            data-testid="competitor-slate-select"
            className="bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-1 mono text-[11px] text-zinc-100"
          >
            {slates.map((s) => (
              <option key={s.dg} value={s.dg}>
                {s.label}{s.is_showdown ? " · SD" : ""} — {s.source_count} source{s.source_count === 1 ? "" : "s"}
              </option>
            ))}
          </select>
          {(() => {
            const sel = slates.find((s) => s.dg === slateDg);
            if (!sel) return null;
            return sel.feeds_optimizer ? (
              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#39FF14]/60 text-[#39FF14] bg-[#39FF14]/10"
                    data-testid="competitor-slate-live">feeds the optimizer</span>
            ) : (
              <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#FF8A00]/60 text-[#FF8A00] bg-[#FF8A00]/10"
                    data-testid="competitor-slate-parked">
                stored for later · apply this slate to build with it
              </span>
            );
          })()}
          <span className="mono text-[9px] text-zinc-600 ml-auto">
            {prettySource(tab)}{Object.keys(entries).length ? ` · ${Object.keys(entries).length} players entered` : ""}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="heading text-lg font-black uppercase inline-flex items-center gap-2">
            <Table size={16} weight="fill" className="text-[#FFCC00]" /> Competitor pools · manual entry
          </div>
          <div className="text-[11px] text-zinc-400 mt-1 max-w-3xl leading-relaxed">
            One tab per source, over the <span className="text-white">active slate's playable pool</span> sorted by salary. Type the site's
            projection and ownership %, tab to the next row — saves automatically. Ownership is a per-slate number, so switch slates before
            entering showdown figures (and clear the source first so main-slate ownership doesn't bleed in). The bootstrap needs ≥40 owned
            players; the top ~150 by salary carries most of the calibration value.
          </div>
        </div>
        <div className="mono text-[10px] uppercase tracking-widest text-right">
          <div className="text-[#FFCC00] font-bold" data-testid="cpg-progress">{prettySource(tab)}: {entered} / {pool.length} entered · {ownEntered} with own%</div>
          {unsaved > 0 && (
            <div className="text-[#FF8A00] mt-0.5" data-testid="cpg-unsaved">{unsaved} unsaved · saving…</div>
          )}
          {coverage && <div className="text-zinc-500 mt-0.5">field model coverage {coverage.coverage_pct}% · {coverage.n_sources} sources</div>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {tabs.map((s) => (
          <button key={s} type="button" onClick={() => { flush(); setTab(s); }} data-testid={`cpg-tab-${s.toLowerCase()}`} aria-pressed={tab === s}
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${tab === s ? "bg-[#FFCC00] text-black border-[#FFCC00] font-bold" : "border-zinc-700 text-zinc-300 hover:border-[#FFCC00]"}`}>
            {prettySource(s)}{sources.includes(s) ? " ●" : ""}
          </button>
        ))}
        <button type="button" onClick={addTab} data-testid="cpg-add-tab" title="Add another source tab"
          className="mono text-[10px] px-2 py-1.5 border border-dashed border-zinc-700 text-zinc-400 hover:border-[#FFCC00] hover:text-[#FFCC00]">+</button>
        <span className="mx-2 text-zinc-700">|</span>
        {POS.map((p) => (
          <button key={p} type="button" onClick={() => setPos(p)} data-testid={`cpg-pos-${p.toLowerCase()}`}
            className={`mono text-[9px] uppercase tracking-widest px-2 py-1 border ${pos === p ? "bg-[#39FF14] text-black border-[#39FF14]" : "border-zinc-800 text-zinc-400 hover:border-zinc-500"}`}>{p}</button>
        ))}
        <label className="relative ml-auto">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name / team" data-testid="cpg-search"
            className="bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none pl-6 pr-2 py-1 mono text-[11px] w-44" />
        </label>
        <select value="" onChange={(e) => copyFrom(e.target.value)} data-testid="cpg-copy-from"
          className="bg-black border border-zinc-800 mono text-[10px] uppercase tracking-widest px-2 py-1 text-zinc-300">
          <option value="">Copy from…</option>
          {sources.filter((s) => s !== tab).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button type="button" onClick={clearTab} data-testid="cpg-clear" className="mono text-[10px] uppercase tracking-widest border border-zinc-800 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-500 px-2 py-1">Clear {prettySource(tab)}</button>
        <CsvPasteImport
          dg={slateDg}
          tab={tab}
          pool={pool}
          onImported={(data) => { setCoverage(data?.coverage || null); loadEntries(tab); loadSources(); }}
        />
      </div>
      <div className="overflow-auto max-h-[60vh] border border-zinc-900 wp-scroll" onBlur={flush} data-testid="cpg-table-wrap">
        <table className="w-full text-[11px] mono">
          <thead className="sticky top-0 bg-[#121212] border-b border-zinc-800 z-10">
            <tr className="text-zinc-500 text-left">
              <th className="p-2 w-8">#</th><th className="p-2">Player</th><th className="p-2">Pos</th><th className="p-2">Team</th>
              <th className="p-2 text-right">Salary</th><th className="p-2 text-right">Ours</th>
              <th className="p-2 text-right text-[#FFCC00]">{prettySource(tab)} proj</th>
              <th className="p-2 text-right text-[#FFCC00]">{prettySource(tab)} {isShowdown ? "FLEX own%" : "own%"}</th>
              {isShowdown && (
                <>
                  <th className="p-2 text-right text-[#FF3B30]" title="The site's captain projection (blank = we use 1.5x the FLEX number)">{prettySource(tab)} CPT proj</th>
                  <th className="p-2 text-right text-[#FF3B30]" title="Captain ownership — the share of lineups that crown this player">{prettySource(tab)} CPT own%</th>
                  <th className="p-2 text-right text-zinc-500" title="Total share of lineups containing this player (CPT + FLEX)">total own%</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.map((p, i) => {
              const e = entries[normKey(p.name)] || {};
              return (
                <tr key={p.dk_id} className={`border-b border-zinc-900 ${e.own != null || e.proj != null ? "bg-[#FFCC00]/5" : ""}`} data-testid={`cpg-row-${p.dk_id}`}>
                  <td className="p-2 text-zinc-600">{i + 1}</td>
                  <td className="p-2 text-white">{p.name}</td>
                  <td className="p-2 text-zinc-400">{p.position}</td>
                  <td className="p-2 text-zinc-400">{p.team}</td>
                  <td className="p-2 text-right text-zinc-300">${p.salary}</td>
                  <td className="p-2 text-right text-zinc-500">{Number(p.our_proj).toFixed(1)} · {Number(p.our_own || 0).toFixed(1)}%</td>
                  <td className="p-1 text-right">
                    <input type="number" step="0.1" min="0" value={e.proj ?? ""} onChange={(ev) => setField(p, "proj", ev.target.value)} placeholder="—"
                      data-testid={`cpg-proj-${p.dk_id}`} className="w-16 bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-0.5 text-right" />
                  </td>
                  <td className="p-1 text-right">
                    <input type="number" step="0.1" min="0" max="100"
                      value={(isShowdown ? e.own_flex : e.own) ?? ""}
                      onChange={(ev) => setField(p, isShowdown ? "own_flex" : "own", ev.target.value)} placeholder="—"
                      data-testid={`cpg-own-${p.dk_id}`} className="w-16 bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-0.5 text-right" />
                  </td>
                  {isShowdown && (
                    <>
                      <td className="p-1 text-right">
                        <input type="number" step="0.1" min="0" value={e.proj_cpt ?? ""} onChange={(ev) => setField(p, "proj_cpt", ev.target.value)} placeholder="1.5×"
                          data-testid={`cpg-proj-cpt-${p.dk_id}`} className="w-16 bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-2 py-0.5 text-right" />
                      </td>
                      <td className="p-1 text-right">
                        <input type="number" step="0.1" min="0" max="100" value={e.own_cpt ?? ""} onChange={(ev) => setField(p, "own_cpt", ev.target.value)} placeholder="—"
                          data-testid={`cpg-own-cpt-${p.dk_id}`} className="w-16 bg-black border border-zinc-800 focus:border-[#FF3B30] outline-none px-2 py-0.5 text-right" />
                      </td>
                      <td className="p-2 text-right text-zinc-500" data-testid={`cpg-own-total-${p.dk_id}`}>
                        {e.own != null ? `${Number(e.own).toFixed(1)}%` : "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-2 mono text-[10px] text-zinc-500">
        <span>Showing {visible.length} of {pool.filter((p) => pos === "ALL" || p.position === pos).length} · sorted by salary</span>
        {limit < pool.length && <button type="button" onClick={() => setLimit(1000)} data-testid="cpg-show-all" className="text-[#FFCC00] hover:underline inline-flex items-center gap-1"><Copy size={10} /> show all</button>}
      </div>
    </section>
  );
}
